<template>
  <Modal
    container-class="!w-[95vw]"
    fullbelowsm
    :title="!editData ? $t('projects.personas.create') : $t('projects.personas.edit')"
    :primary-button="$t('save')"
    :secondary-button="$t('cancel')"
    :secondary-button-disabled="loading"
    :primary-button-loading="loading"
    :primary-button-disabled="!name || !value || loading"
    :loading="fetching"
    @primary="onSaveClick"
    @secondary="onCancelClick"
    @opened="onModalOpen"
  >
    <div class="w-full max-w-full min-h-full md:min-h-600 flex flex-col gap-y-3 px-10">
      <FormInput v-model="name" ref="name" :label="$t('projects.personas.name')" :readonly="loading" />

      <div class="w-full min-h-21 flex flex-col justify-start flex-shrink-0 flex-grow">
        <FormLabel :label="$t('value')" />
        <div class="flex flex-row flex-grow gap-x-4 overflow-hidden">
          <div class="w-full relative border flex-grow flex flex-col">
            <div class="flex h-5">
              <div class="text-13 cursor-pointer w-auto px-3 text-center border-r" :class="{ 'border-b': tab !== 'editor' }" @click="tab = 'editor'">Markdown</div>
              <div class="flex-grow border-b"></div>
            </div>
            <div class="flex-grow grid w-full">
              <div v-show="tab === 'editor'">
                <MonacoEditor v-model:value="value" :options="options" width="100%" height="100%" class="flex-grow h-full" language="markdown" @mount="monacoMounted"></MonacoEditor>
              </div>
            </div>
          </div>
          <div class="max-h-500 flex flex-col w-300 gap-y-3 select-none">
            <div class="flex-grow w-full border flex flex-col overflow-hidden">
              <div class="px-1 border-b sticky top-0 bg-stroke bg-opacity-20 text-12 font-500 py-0.5">Variables</div>
              <div class="overflow-auto text-13">
                <div
                  v-for="variable in variables"
                  :key="variable.name"
                  class="px-1 flex cursor-pointer"
                  :class="{ 'bg-secondary text-off-white': selectedVariable === variable.name }"
                  @click="selectedVariable = variable.name"
                  @dblclick="onVariableDblClick(variable)"
                >
                  <div class="truncate flex-grow">{{ variable.name }}</div>
                  <Icon v-if="selectedVariable === variable.name" name="plus" class="w-4 flex-shrink-0" />
                </div>
                <div v-if="!variables.length" class="px-1 text-12 text-gray-400 py-1">No variables</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<script>
import MonacoEditor from '@guolao/vue-monaco-editor';
import { mapActions, mapGetters } from 'vuex';
import markdownit from 'markdown-it';

export default {
  name: 'CreatePersonaModal',
  components: { MonacoEditor },
  props: {
    projectId: {
      type: String,
      required: true,
    },
    editData: {
      type: Object,
    },
  },
  data() {
    return {
      loading: false,
      fetching: false,
      name: '',
      nameError: '',
      text: '',
      textError: '',
      tab: 'editor',
      value: null,
      personaId: null,
      editor: null,
      completionProvider: null,
      selectedVariable: null,
      options: {
        formatOnType: true,
        formatOnPaste: true,
        wordWrap: 'on',
      },
    };
  },
  computed: {
    ...mapGetters(['getProjectVariables']),
    variables() {
      const variableList = this.getProjectVariables();
      return Object.keys(variableList)
        .map((key) => ({ name: key, ...variableList[key] }))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    },
  },
  async created() {
    if (this.editData) {
      const { name, persona_id } = this.editData;
      this.name = name;
      this.personaId = persona_id;

      this.fetching = true;
      const response = await this.fetchProjectPersona({ projectId: this.projectId, personaId: this.personaId });
      if (response) {
        this.value = response.value;
      }
      this.fetching = false;
    }
  },
  unmounted() {
    this.completionProvider?.dispose?.();
    this.editor?.dispose?.();
  },
  methods: {
    ...mapActions(['showToastMessage', 'createProjectPersona', 'updateProjectPersona', 'fetchProjectPersona']),
    onModalOpen() {
      this.$refs?.name?.focus();
    },
    monacoMounted(editor, monaco) {
      this.editor = editor;
      this.completionProvider = monaco.languages.registerCompletionItemProvider('markdown', {
        provideCompletionItems: () => ({
          suggestions: this.variables.map((item) => ({
            label: item.name,
            insertText: `{${item.name}}`,
            kind: 17,
          })),
        }),
      });
    },
    onVariableDblClick(variable) {
      if (!this.editor) {
        return;
      }
      this.editor.focus();
      const selection = this.editor.getSelection();
      const id = { major: 1, minor: 1 };
      const op = { identifier: id, range: selection, text: `{${variable.name}}`, forceMoveMarkers: true };
      this.editor.executeEdits('my-source', [op]);
    },
    async onSaveClick() {
      this.loading = true;
      if (this.editData) {
        const response = await this.updateProjectPersona({ project_id: this.projectId, persona_id: this.personaId, name: this.name, value: this.value });
        if (response) {
          this.$emit('close');
        }
      } else {
        const response = await this.createProjectPersona({ project_id: this.projectId, name: this.name, value: this.value });
        if (response) {
          this.$emit('close');
        }
      }
      this.loading = false;
    },
    onCancelClick() {
      this.$emit('close');
    },
  },
  watch: {
    async tab() {
      if (this.tab === 'preview') {
        const md = markdownit();
        this.$refs.preview.innerHTML = md.render(this.value);
      }
    },
  },
};
</script>

<style scoped></style>
